import styled from 'styled-components';
import {
  CanvasElementLayers,
} from 'flow/service/graph';
import {
  SIMPLE_ISLAND_EXPANDED_RIGHT,
  SIMPLE_ISLAND_NODE_SPACING,
  SIMPLE_ISLAND_PEEKING_RIGHT,
  SIMPLE_ISLAND_TOP,
} from './constants';
import type { IslandMode } from './index';


interface StyledBaseSimpleIslandContainerProps {
  islandMode: IslandMode;
}

/**
 * Edges going into outcome and workflow routing nodes are usually hidden except when hovering or
 * clicking on a them.
 *
 * These nodes seen in the canvas are actually placed outside of it and inside of the
 * Island component - this helps keeping the outcomes & workflow routing nodes fixed as the user
 * scrolls around without having to update the layout on every move event. Since those nodes are
 * not part of react-flow land, they cannot connect to other nodes, so we use "construction" nodes
 * to render an invisible react-flow node that is placed at the same position as the OnIslandOutcomeNode
 * & OnIslandWorkflowRouting node, giving other nodes something to connect to and simulating
 * node connections to Island nodes.
 *
 * Instead of exactly aligning outcome & workflow routing construction nodes to the island nodes,
 * construction nodes are always placed outside of the screen, flush with the right-side border of it,
 * and vertically placed at the same position as their corresponding node in the Island.
 * We place nodes horizontally outside of the canvas, and instead of exactly under the on-island node,
 * because otherwise exactly aligning them with island nodes is very complex and unnecessary.
 *
 * We need to show whitespace between the right side of the SimpleIsland and the right
 * border of the screen, and since these construction nodes are outside of the screen, we
 * need to somehow stop the edge from bleeding outside of the SimpleIsland and out of the
 * screen.
 *
 * This container applies a few simple CSS tricks to hide the bleeding edges:
 *    - when expanded, align the node to the right border of the page (`right:0`)
 *    - when expanded, grow the SimpleIsland to render a whitespace up until the
 *     right-hand side of the screen, covering the edge that is rendered under it.
 *     (`padding-right: SIMPLE_ISLAND_EXPANDED_RIGHT`)
 */
export const StyledBaseSimpleIslandContainer = styled.div<StyledBaseSimpleIslandContainerProps>`
  position: absolute;
  background-color: ${p => (p.islandMode === 'peeking' ? 'transparent' : 'white')};
  /* offset up by the same amount of padding-top space */
  top: calc(${SIMPLE_ISLAND_TOP}px - var(--cl-space-1));
  /* position animations are known to be choppy on absolute positioned elements.
    if needed, use translateX instead of conditionally setting a "right" position.
  */
  right: ${p => (p.islandMode === 'expanded'
    // when expanded, render the island flush to the right-side of the screen
    ? 0
    : SIMPLE_ISLAND_PEEKING_RIGHT)}px;
  z-index: ${CanvasElementLayers.PANELS};
  padding: ${p => (p.islandMode === 'expanded'
    // create the island "spacing" with padding
    ? `var(--cl-space-1) ${SIMPLE_ISLAND_EXPANDED_RIGHT}px var(--cl-space-1) 0px;`
    : 'var(--cl-space-1) 0px var(--cl-space-1) 0px;'
  )};
  border-radius: var(--cl-space-1);
  transition: all 200ms ease-in-out;
  /* some wrappers might change the cursor, so we set it again here to guarantee it's always the default one */
  cursor: default;
`;

export const NodeContainer = styled.div`
  /*  apply rule to every direct children except the first one*/
  & > :not(:first-child) {
    margin-top: ${SIMPLE_ISLAND_NODE_SPACING}px;
  }
`;
